import { bindGlobalErrors } from './global-errors'
import {
  type Tel,
  type SpanData,
  type StartSpanOpts,
  type InstrumentedSpanOpts,
  type ResourceUserInfo,
  type Span,
  type TelResources,
  type TraceData,
  type TelParent,
  getBaggage,
  getLogBaggage,
} from './tel-types'
import type { RootTel } from './tel-types'
export type { RootTel }
import { isProd, mapError, ulid } from '@w/utils'
import type { DDSketch } from './ddsketch'
import type { TelErrorData } from './tel-error-data'
import {
  createTelStorageCore,
  type TelExporter,
  type TelStorage,
  type BoundTelExporter,
  type TelStorageEventHandlers,
  type TelStorageBindable,
  type TelStorageConfig,
} from './tel-storage'
import { ALWAYS_EXPORT_LOG_ATTRIBUTE } from './tel-types'
import { loadFuncMapStackResolver } from './client-error-groups'
import { prepareTelError } from './prepare-for-log/prepare-tel-error'
import { createTelResource } from './create-tel-resource'
import { DEFAULT_HISTOGRAM_BUCKETS, _createMetricsFactory } from './tel-metrics'
import { _createSpan } from './span'
import type { ReadFuncMapsResult } from './read-func-maps-types'

// Error-group fingerprints hash the full first-class frame chain. Foreign
// frames (node internals, deps) are excluded by identity, but V8's default
// stackTraceLimit=10 lets them consume the capture budget and push
// first-class frames off the END of the stack - frames never captured cannot
// be classified back in, and the truncated chain splits the group. Raising
// the limit is what makes the uncapped chain deterministic. Residual: stacks
// deeper than 50 can still truncate-split; accepted (split-biased design).
const MIN_STACK_TRACE_LIMIT = 50

type CreateRootTelBaseOpts = Omit<
  TelStorageConfig,
  'errorGroupScope' | 'stackResolver'
> & {
  name: string
  origin: 'client' | 'server'
  env: string
  sessionId?: string
  userAgent?: SpanData['userAgent']
}

type CreateRootTelFuncMapsOpts = CreateRootTelBaseOpts & {
  funcMaps: {
    read: () => Promise<ReadFuncMapsResult>
    onError?: (error: unknown) => void
  }
}

export function createRootTel(opts: CreateRootTelBaseOpts): RootTel
export function createRootTel(opts: CreateRootTelFuncMapsOpts): Promise<RootTel>
export function createRootTel(
  opts: CreateRootTelBaseOpts | CreateRootTelFuncMapsOpts,
): RootTel | Promise<RootTel> {
  ensureErrorStackTraceLimit()

  // Auto-create resource
  const resource = createTelResource({
    name: opts.name,
    origin: opts.origin,
    env: opts.env,
  })
  const errorGroupScope = `${resource.deploy.name}:${resource.deploy.env}`
  const funcMaps = 'funcMaps' in opts ? opts.funcMaps : undefined
  if (funcMaps) {
    return loadFuncMapStackResolver({
      load: async () => (await funcMaps.read()).maps,
      onError: funcMaps.onError,
    }).then((stackResolver) =>
      createRootTelFromConfig({
        opts,
        resource,
        errorGroupScope,
        stackResolver,
      }),
    )
  }
  return createRootTelFromConfig({
    opts,
    resource,
    errorGroupScope,
    stackResolver: undefined,
  })
}

function ensureErrorStackTraceLimit(): void {
  const errorCtor = Error as ErrorConstructor & { stackTraceLimit?: number }
  if (
    typeof errorCtor.stackTraceLimit === 'number' &&
    errorCtor.stackTraceLimit >= MIN_STACK_TRACE_LIMIT
  ) {
    return
  }
  errorCtor.stackTraceLimit = MIN_STACK_TRACE_LIMIT
}

function createRootTelFromConfig(input: {
  opts: CreateRootTelBaseOpts & { funcMaps?: unknown }
  resource: TelResources
  errorGroupScope: string
  stackResolver?: NonNullable<TelStorageConfig['stackResolver']>
}): RootTel {
  const { opts, resource, errorGroupScope, stackResolver } = input
  const {
    name: _name,
    origin: _origin,
    env: _env,
    sessionId: _sessionId,
    userAgent: _userAgent,
    funcMaps: _funcMaps,
    ...storageConfig
  } = opts
  const storage = createTelStorageCore({
    ...storageConfig,
    recentTracesLimit:
      storageConfig.recentTracesLimit ?? (opts.origin === 'client' ? 2 : 0),
    errorGroupScope,
    stackResolver,
  })
  if (isProd) {
    console.info(_formatRootTelResourceLog(resource.deploy))
  }

  // Root span is never stored or exported since it never ends
  // Create span directly without going through storage
  const rootSpanData: SpanData = {
    userAgent: opts.userAgent,
    id: 'root',
    sessionId: opts.origin === 'client' ? opts.sessionId : undefined,
    name: `root-${opts.origin}`,
    origin: opts.origin,
    parentId: undefined,
    requestId: undefined,
    traceId: undefined,
    traceFlags: undefined,
    traceState: undefined,
    input: undefined,
  }

  // Create isolated span that doesn't interact with storage
  const rootSpan = _createSpan(rootSpanData, {
    end: () => {}, // Root span never ends
    update: () => {}, // Root span never updates
    errors: { create: () => {} }, // Root span errors handled via child spans
  })

  const root = createRootTelWithMetrics(rootSpan, storage, resource, opts)
  const releaseGlobalErrors = bindGlobalErrors(root)
  const shutdown = root.shutdown.bind(root)
  root.shutdown = () => {
    releaseGlobalErrors()
    return shutdown()
  }
  return root
}

export function createRouteTel(
  rootTel: RootTel,
  prev: Tel,
  pathname: string,
): RootTel {
  const id = ulid()
  const storage = rootTel._storage

  const rootTelData = rootTel.currentSpan.data

  const previousRouteTel = prev
  let previousId = previousRouteTel.currentSpan.data.id
  if (previousRouteTel) {
    previousRouteTel.currentSpan.end({})
    previousId = previousRouteTel.currentSpan.data.id
  }

  const routeSpan = storage.spans.create(
    {
      id,
      traceId: id,
      name: pathname,
      target: 'router',
      previousId,
      origin: 'client', // Explicitly set client origin for route spans
      // User data will be automatically populated from the user data store
    },
    {
      sessionId: rootTelData.sessionId,
    },
  )

  return createRootTelWithMetrics(routeSpan, storage, rootTel.resource)
}

// Internal factory that creates RootTel with metrics namespace
function createRootTelWithMetrics(
  span: Span,
  storage: TelStorage,
  resource: TelResources,
  metricOpts: Pick<TelStorageConfig, 'metricSeriesLimit' | 'metricSeriesIdleIntervals' | 'metricSketchMaxBins' | 'metricsMode' | 'perfMetricsAggregationInterval'> = {},
): RootTel {
  const currentStorage = storage
  const tel = createTel(span, () => currentStorage, undefined, resource.deploy)

  const metricsFactory = _createMetricsFactory(() => currentStorage, resource.metrics, {
    metricSeriesLimit: metricOpts.metricSeriesLimit,
    metricSeriesIdleIntervals: metricOpts.metricSeriesIdleIntervals,
    metricSketchMaxBins: metricOpts.metricSketchMaxBins,
    mode: metricOpts.metricsMode,
  })
  const shutdownCallbacks = new Set<() => Promise<void>>()

  type PerfHistogram = ReturnType<typeof metricsFactory.histogram>
  const perfHistograms = new Map<
    string,
    { throughput: PerfHistogram; duration: PerfHistogram }
  >()

  currentStorage.setPerfCallback((endedSpan) => {
    const id = endedSpan.performanceMetricId
    if (!id) return
    if (!perfHistograms.has(id)) {
      perfHistograms.set(id, {
        throughput: metricsFactory.histogram({
          name: `perf.${id}.throughput`,
          unit: 'By/ms',
          bucketBoundaries: DEFAULT_HISTOGRAM_BUCKETS,
          aggregationInterval: metricOpts.perfMetricsAggregationInterval ?? 60_000,
        }),
        duration: metricsFactory.histogram({
          name: `perf.${id}.duration`,
          unit: 'ms',
          bucketBoundaries: DEFAULT_HISTOGRAM_BUCKETS,
          aggregationInterval: metricOpts.perfMetricsAggregationInterval ?? 60_000,
        }),
      })
    }
    const h = perfHistograms.get(id)
    if (!h) return
    const dur = endedSpan.duration ?? 0
    const totalSize = (endedSpan.inputSize ?? 0) + (endedSpan.outputSize ?? 0)
    const exemplar = {
      traceId: endedSpan.traceId ?? endedSpan.id,
      spanId: endedSpan.id,
      requestId: endedSpan.requestId,
      ...(endedSpan.otel ? { otel: { traceId: endedSpan.otel.traceId } } : {}),
    }
    const attrs = { operation: endedSpan.name }

    if (totalSize > 0 && dur > 0) {
      h.throughput._recordWithExemplar(totalSize / dur, attrs, exemplar)
    } else {
      h.duration._recordWithExemplar(dur, attrs, exemplar)
    }
  })

  return {
    ...tel,
    resource,
    get _storage() {
      return currentStorage
    },
    setHandlers(...handlers: TelStorageBindable[]) {
      const bound = handlers.flatMap((handler) => {
        const result = _bindHandler(handler, resource, currentStorage)
        return '_bindings' in result && result._bindings
          ? result._bindings
          : [result]
      })
      currentStorage.setHandlers(...bound)
      for (const handler of bound) {
        if (_hasFlush(handler)) {
          currentStorage.registerFlush(handler.flush)
        }
        if (_hasShutdown(handler)) {
          shutdownCallbacks.add(handler.shutdown)
        }
      }
    },
    async flush() {
      metricsFactory.flushAggregators()
      await currentStorage.flush()
    },
    async shutdown() {
      metricsFactory.flushAggregators()
      metricsFactory.clearAggregators()
      await currentStorage.flush()
      await Promise.all(
        Array.from(shutdownCallbacks).map(async (callback) => {
          try {
            await callback()
          } catch {
            // Keep shutdown best-effort across exporters.
          }
        }),
      )
      currentStorage.stopTimers()
    },
    counter: metricsFactory.counter,
    upDownCounter: metricsFactory.upDownCounter,
    gauge: metricsFactory.gauge,
    histogram: metricsFactory.histogram,
    exponentialHistogram: metricsFactory.exponentialHistogram,
  }
}

function detachedSource(
  parent: UserInfoSource,
  overrides: Partial<ResourceUserInfo>,
): UserInfoSource {
  const detached = { ...parent.get(), ...overrides }
  return { get: () => detached }
}

function resolveStartSpanData(
  opts: Omit<InstrumentedSpanOpts, 'userInfo'>,
  origin: SpanData['origin'],
  parentSpanData: SpanData,
  fallbackId = ulid(),
) {
  const { trace, request, traceId, requestId, ...rest } = opts
  const id = rest.id ?? fallbackId
  // Top-level span: created directly on root tel with no inherited traceId.
  // It owns its own trace boundary as if `trace: true` was passed.
  const isTopLevel = parentSpanData.id === 'root' && !parentSpanData.traceId
  return {
    ...rest,
    id,
    origin,
    traceId: traceId ?? (trace || isTopLevel ? id : undefined),
    requestId: requestId ?? (request ? id : undefined),
  }
}

function getSpanParentData(spanData: SpanData): TelParent {
  if (spanData.id !== 'root') return spanData
  const { id: _rootId, ...parentData } = spanData
  return parentData
}

interface UserInfoSource {
  get(): ResourceUserInfo
}

function createTel(
  currentSpan: Span,
  getStorage: () => TelStorage,
  inheritedUserInfo?: UserInfoSource,
  resource?: TelResources['deploy'],
): Tel {
  const logBaggage = getLogBaggage(currentSpan.data)
  const origin = currentSpan.data.origin // Use span's origin instead of global

  // Copy-on-write: reads from inherited source until setUserInfo detaches
  const parentSource: UserInfoSource = inheritedUserInfo ?? {
    get: () => getStorage().userDataStore.get(),
  }
  let localUserInfo: ResourceUserInfo | null = null

  const userInfoSource: UserInfoSource = {
    get() {
      return localUserInfo ?? parentSource.get()
    },
  }

  async function span<R>(
    opts: StartSpanOpts,
    cb: (tel: Tel) => R,
  ): Promise<Awaited<R>> {
    if (!opts || typeof opts !== 'object' || !('input' in opts)) {
      throw new TypeError('span() requires options with explicit input')
    }
    const storage = getStorage()
    const childSpan = storage.spans.create(
      resolveStartSpanData(opts, currentSpan.data.origin, currentSpan.data),
      getSpanParentData(currentSpan.data),
      userInfoSource.get(),
    )
    const tel = createTel(childSpan, () => storage, userInfoSource, resource)
    try {
      const result = await cb(tel)
      childSpan.end({ output: result })
      return result
    } catch (error) {
      childSpan.end({ error })
      throw error
    }
  }

  return {
    info(body, attrs) {
      const storage = getStorage()
      storage.logs.create({
        id: ulid(),
        body,
        level: 'info',
        origin,
        ...logBaggage,
        attributes: attrs,
      })
    },
    audit(body, attrs) {
      const storage = getStorage()
      storage.logs.create({
        id: ulid(),
        body,
        level: 'info',
        origin,
        ...logBaggage,
        attributes: {
          ...attrs,
          [ALWAYS_EXPORT_LOG_ATTRIBUTE]: true,
        },
      })
    },
    error(value, attrs) {
      const storage = getStorage()
      const id = ulid()
      const isMessage = typeof value === 'string'
      const error = isMessage ? new Error(value) : mapError(value)
      const errorData = isMessage
        ? ({
            origin: origin ?? 'client',
            level: 'error',
            logId: id,
            traceId: logBaggage.traceId,
            requestId: logBaggage.requestId,
            error: {
              type: 'LogError',
              message: value,
              stack: error instanceof Error ? error.stack : undefined,
            },
          } as TelErrorData)
        : prepareTelError({ error, span: this.currentSpan.data })
      errorData.logId = id
      const userInfo = userInfoSource.get()
      storage.logs.create({
        id,
        body: errorData.error.message,
        level: 'error',
        origin,
        ...logBaggage,
        error: errorData,
        userId: userInfo.userId,
        companyId: userInfo.companyId,
        attributes: attrs,
      })
      storage.spans.errors.create(error, errorData, userInfo)
      return value
    },
    warn(body, attrs) {
      const storage = getStorage()
      storage.logs.create({
        id: ulid(),
        body,
        level: 'warn',
        origin,
        ...logBaggage,
        attributes: attrs,
      })
    },
    debug(body, attrs) {
      const storage = getStorage()
      storage.logs.create({
        id: ulid(),
        body,
        level: 'debug',
        origin,
        ...logBaggage,
        attributes: attrs,
      })
    },

    _createSpan(opts) {
      const storage = getStorage()
      const { userInfo: optsUserInfo, ...spanOpts } = opts
      const resolvedUserInfo = optsUserInfo
        ? { ...userInfoSource.get(), ...optsUserInfo }
        : userInfoSource.get()
      return storage.spans.create(
        resolveStartSpanData(spanOpts, this.currentSpan.data.origin, this.currentSpan.data),
        getSpanParentData(this.currentSpan.data),
        resolvedUserInfo,
      )
    },

    _createTel(opts) {
      const storage = getStorage()
      const { userInfo: optsUserInfo, ...spanOpts } = opts
      const childSource = optsUserInfo
        ? detachedSource(userInfoSource, optsUserInfo)
        : userInfoSource
      const span = storage.spans.create(
        resolveStartSpanData(spanOpts, this.currentSpan.data.origin, this.currentSpan.data),
        getSpanParentData(this.currentSpan.data),
        childSource.get(),
      )
      return createTel(span, () => storage, childSource, resource)
    },

    _exportBoundary() {
      const { duration, timestamp } = currentSpan.data
      if (duration === undefined || timestamp === undefined) {
        throw new Error('_exportBoundary requires an ended span')
      }
      const storage = getStorage()
      const trace = storage._reconstructSpan(currentSpan.data.id) ?? {
        ...currentSpan.data,
        logs: [],
        children: [],
      }
      return structuredClone({
        trace,
        metrics: storage._drainMetricsSince(timestamp - duration),
        resource: {
          name: resource?.name ?? '',
          env: resource?.env ?? '',
          origin: resource?.origin ?? currentSpan.data.origin ?? 'client',
          ...(resource?.version ? { version: resource.version } : {}),
        },
      })
    },

    _insertBoundaryTrace(trace, metrics, producerResource, completion) {
      const storage = getStorage()
      const baggage = this._getBaggage()
      const userInfo = this.getUserInfo()
      const resourceAttributes = producerResource
        ? {
            'tel.resource.name': producerResource.name,
            'tel.resource.env': producerResource.env,
            'tel.resource.origin': producerResource.origin,
            ...(producerResource.version
              ? { 'tel.resource.version': producerResource.version }
              : {}),
          }
        : undefined

      // Foreign request IDs identify producer-local boundaries. Rebased errors
      // must share the caller's occurrence identity, just like their spans.
      function rebaseError(error: TraceData['error']): TraceData['error'] {
        if (!error || completion?.preserveParent) return error
        return { ...error, traceId: baggage.traceId ?? error.traceId, requestId: baggage.requestId }
      }

      function insert(node: TraceData, parentId?: string): boolean {
        if (storage._hasSpan(node.id)) {
          // INV-6: snapshot replacement is explicit caller policy, never a span name.
          if (!completion?.updateExisting) return false
          const { logs: _logs, children, ...patch } = node
          if (!completion.preserveParent) {
            patch.parentId = parentId
            patch.traceId = baggage.traceId ?? patch.traceId
            patch.sessionId = baggage.sessionId ?? patch.sessionId
            delete patch.requestId
          }
          if (patch.error) patch.error = rebaseError(patch.error)
          storage.spans.update(patch)
          for (const child of children) insert(child, node.id)
          return true
        }
        const { logs, children, ...foreignSpan } = node
        const spanData: SpanData = {
          ...foreignSpan,
          origin: producerResource?.origin ?? foreignSpan.origin,
          attributes: {
            ...foreignSpan.attributes,
            ...resourceAttributes,
          },
          parentId,
          traceId: completion?.preserveParent ? foreignSpan.traceId : baggage.traceId ?? foreignSpan.traceId,
          sessionId: completion?.preserveParent ? foreignSpan.sessionId : baggage.sessionId ?? foreignSpan.sessionId,
          userId: foreignSpan.userId ?? userInfo.userId,
          companyId: foreignSpan.companyId ?? userInfo.companyId,
        }
        delete spanData.requestId
        if (spanData.error) spanData.error = rebaseError(spanData.error)
        storage.spans.insertEnded(spanData)

        for (const foreignLog of logs) {
          const log = {
            ...foreignLog,
            origin: producerResource?.origin ?? foreignLog.origin,
            attributes: {
              ...foreignLog.attributes,
              ...resourceAttributes,
            },
            traceId: spanData.traceId,
            sessionId: spanData.sessionId,
          }
          delete log.requestId
          if (log.error) log.error = rebaseError(log.error)
          storage.logs.insert(log)
        }
        for (const child of children) insert(child, spanData.id)
        return true
      }

      // The literal root pseudo-id ('root', never a real stored span) must
      // never reach a span's `parentId` -- `baggage.parentId` already
      // encodes that guard (see `getBaggage`), unlike `currentSpan.data.id`
      // directly. Inserting under the literal string here would leave the
      // boundary's top node structurally unreachable from any real span
      // (its own new, self-rooted trace instead of nesting under nothing).
      if (insert(trace, completion?.preserveParent ? trace.parentId : baggage.parentId)) {
        for (const metric of metrics) storage.metrics.create(metric)
        // INV-2: producer diagnostic custody begins after its complete ended tree is inserted.
        const failure = findFailure(trace)
        if (completion?.error !== undefined && failure) {
          storage.spans.errors.create(mapError(completion.error), rebaseError(failure) ?? failure, userInfo, completion)
        }
      }

      function findFailure(node: TraceData): TraceData['error'] {
        if (node.error) return node.error
        for (const child of node.children) {
          const failure = findFailure(child)
          if (failure) return failure
        }
        return undefined
      }
    },

    _getBaggage() {
      return getBaggage(currentSpan.data)
    },

    _clear() {
      const storage = getStorage()
      storage.clear()
    },

    // Copy-on-write: reads from inherited store (root's userDataStore) until
    // setUserInfo is called, which detaches into a local copy.
    // This supports both browser (shared store, user logs in later) and
    // stateful servers (per-request isolation via _createTel({ userInfo })).
    setUserInfo(info) {
      localUserInfo ??= { ...parentSource.get() }
      Object.assign(localUserInfo, info)
      // Also bind user info to the current span (already created before setUserInfo)
      currentSpan.update({ id: currentSpan.data.id, ...info })
    },

    getUserInfo() {
      return { ...userInfoSource.get() }
    },

    async flush() {
      const storage = getStorage()
      await storage.flush()
    },

    span,
    currentSpan,
  }
}

const nullStorage = createTelStorageCore()

const nullMetricFactories: Pick<
  RootTel,
  'counter' | 'upDownCounter' | 'gauge' | 'histogram' | 'exponentialHistogram'
> & {
  flushAggregators: () => void
  clearAggregators: () => void
} = {
  counter: () => ({
    add: () => {},
    getValue: () => 0,
    clear: () => {},
    _data: { getCurrentValue: () => 0, getAggregator: () => null },
  }),
  upDownCounter: () => ({
    add: () => {},
    getValue: () => 0,
    clear: () => {},
    _data: { getCurrentValue: () => 0, getAggregator: () => null },
  }),
  gauge: () => ({
    set: () => {},
    record: () => {},
    getValue: () => 0,
    _data: { getCurrentValue: () => 0, getAggregator: () => null },
  }),
  histogram: () => ({
    record: () => {},
    _recordWithExemplar: () => {},
    clear: () => {},
    recordDuration: <R>(cb: () => R | Promise<R>) => cb(),
    // oxlint-disable-next-line unicorn/no-useless-undefined -- explicit return value for type inference
    getPercentiles: () => undefined,
    getStatistics: () => ({ count: 0, sum: 0, percentiles: undefined }),
    _data: {
      getStatistics: () => ({
        buckets: [],
        overflowCount: 0,
        sum: 0,
        count: 0,
        min: undefined,
        max: undefined,
        percentiles: undefined,
      }),
      getAggregator: () => null,
      getSketch: () => ({}) as DDSketch,
    },
  }),
  exponentialHistogram: () => ({
    record: () => {},
    _data: {
      getStatistics: () => ({
        scale: 0,
        zeroCount: 0,
        positiveOffset: 0,
        positiveBuckets: [],
        negativeOffset: 0,
        negativeBuckets: [],
        sum: 0,
        count: 0,
        min: undefined,
        max: undefined,
      }),
      getAggregator: () => null,
    },
  }),
  flushAggregators: () => {},
  clearAggregators: () => {},
}

function runNullSpan<R>(
  opts: StartSpanOpts,
  cb: (tel: Tel) => R,
): Promise<Awaited<R>> {
  if (!opts || typeof opts !== 'object' || !('input' in opts)) {
    return Promise.reject(
      new TypeError('span() requires options with explicit input'),
    )
  }
  return Promise.resolve(cb(nullTel))
}

const nullCurrentSpan: Span = {
  data: { id: '', name: '', target: 'router' } satisfies SpanData,
  update: () => {},
  end: () => {},
  _getBaggage: () => ({}),
}

export const nullTel: RootTel = {
  info: () => {},
  audit: () => {},
  error: (value) => value,
  warn: () => {},
  debug: () => {},

  _createSpan: () => nullTel.currentSpan,
  _createTel: () => nullTel,
  _exportBoundary: () => ({
    trace: { ...nullTel.currentSpan.data, logs: [], children: [] },
    metrics: [],
    resource: { name: '', env: '', origin: 'client' },
  }),
  _insertBoundaryTrace: () => {},
  _getBaggage: () => ({}),

  _clear: () => {},
  setUserInfo: () => {},
  getUserInfo: () => ({}),
  flush: () => Promise.resolve(),
  shutdown: () => Promise.resolve(),
  _storage: nullStorage,
  resource: {
    deploy: { name: '', version: '', env: '', origin: 'client' },
    metrics: { name: '', env: '', origin: 'client' },
  },
  setHandlers: () => {},

  counter: nullMetricFactories.counter,
  upDownCounter: nullMetricFactories.upDownCounter,
  gauge: nullMetricFactories.gauge,
  histogram: nullMetricFactories.histogram,
  exponentialHistogram: nullMetricFactories.exponentialHistogram,

  span: runNullSpan,
  currentSpan: nullCurrentSpan,
}

function _bindHandler(
  handler: TelStorageBindable,
  resource: TelResources,
  storage: TelStorage,
) {
  if (_isTelExporter(handler)) return handler.bind(resource, storage)
  return handler
}

function _isTelExporter(handler: TelStorageBindable): handler is TelExporter {
  return 'bind' in handler && typeof handler.bind === 'function'
}

function _hasFlush(
  handler: TelStorageEventHandlers | BoundTelExporter,
): handler is BoundTelExporter & { flush: () => Promise<void> } {
  return 'flush' in handler && typeof handler.flush === 'function'
}

function _hasShutdown(
  handler: TelStorageEventHandlers | BoundTelExporter,
): handler is BoundTelExporter & { shutdown: () => Promise<void> } {
  return 'shutdown' in handler && typeof handler.shutdown === 'function'
}

function _formatRootTelResourceLog(resource: TelResources['deploy']): string {
  return `${resource.name} ${resource.version} ${resource.env}`
}

// User data management API - works with tel instance
export function setTelUserData(tel: RootTel, data: ResourceUserInfo): void {
  tel._storage.userDataStore.set(data)
}

export function updateTelUserData(
  tel: RootTel,
  data: Partial<ResourceUserInfo>,
): void {
  tel._storage.userDataStore.update(data)
}

export function getTelUserData(tel: RootTel): ResourceUserInfo {
  return tel._storage.userDataStore.get()
}

export function subscribeTelUserData(
  tel: RootTel,
  callback: (data: ResourceUserInfo) => void,
): () => void {
  return tel._storage.userDataStore.subscribe(callback)
}

export function setTelUserDataSubscription(
  tel: RootTel,
  userDataConfig: {
    initialData: ResourceUserInfo
    subscribeToUserDataChange: (
      cb: (data: ResourceUserInfo) => void,
    ) => () => void
  },
): () => void {
  // Set the initial user data in the store
  tel._storage.userDataStore.set(userDataConfig.initialData)

  // Subscribe to user data changes and update the store
  // Only subscribe if initial data doesn't have userId
  if (!userDataConfig.initialData.userId) {
    let hasUpdated = false
    return userDataConfig.subscribeToUserDataChange((data) => {
      if (!hasUpdated && data.userId) {
        hasUpdated = true
        tel._storage.userDataStore.update(data)
      }
    })
  }

  // Return empty unsubscribe function if no subscription is needed
  return () => {}
}

// for ctx propagation tel should be passed explicitly.
// Cons: argument drill. Mitigation: use instrumentation. dizzle is not possible to instrument. instrumentation can be also challenging.
// for node js we have AsyncLocalStorage that allows asynchronous ctx propagation.
// However, its best polyfill for browser (https://github.com/langchain-ai/langgraphjs/issues/879#issuecomment-2749252465)
// has the next issues: Parallel and Nested async function Runs are not supported. Basically, it's useless because this are very common use cases.
// Opentelemetry uses deprecated angular package that patches a lot of browser apis to make async propagation work https://github.com/angular/angular/tree/main/libs/zone.js#development-status-of-zonejs
